import {
  Dialog,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogPrimitiveContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'
import { X } from 'lucide-react'
import { ComponentProps, MouseEventHandler, ReactNode } from 'react'

type ViverseDialogProps = ComponentProps<typeof Dialog> & {
  dialogTrigger?: ReactNode
  contentClassName?: string
  triggerClassName?: string
  childrenClassName?: string
  titleClassName?: string
  title?: string
  onClose?: MouseEventHandler<HTMLButtonElement>
  isOpened?: boolean
  withCloseButton?: boolean
  withSeparator?: boolean
  clickOutsideToClose?: boolean
  portalContainer?: HTMLElement | null
}

// 🥷🏼 Use arbitrary values (e.g., size-[28px], p-[20px]) because Tailwind tokens don't work in fullscreen portal
const dialogVariants = cva('bg-[#1D1D24]', {
  variants: {
    variant: {
      default: '',
      overlay: 'bg-[#000000B2]',
      content:
        'w-max max-w-[calc(100%_-_40px)] max-h-[calc(100dvh_-_64px)] overflow-auto fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-overlay grid rounded-2xl gap-[20px] border border-vive-primary-70 lg:min-w-[600px]',
      title:
        'p-[20px] h-[62px] mx-auto 1.5xl:h-[70px] z-navigation sticky top-0 w-full flex items-center justify-between text-vive-white subheader-1 1.5xl:header-4 flex flex-col',
      close: 'size-[28px]',
      children: 'dialog-scrollbar py-0 size-full flex flex-col items-center',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

function ViverseDialog({
  dialogTrigger,
  title = '',
  onClose,
  children,
  contentClassName,
  triggerClassName,
  childrenClassName,
  titleClassName,
  clickOutsideToClose = true,
  withCloseButton = true,
  withSeparator = true,
  portalContainer,
  ...props
}: ViverseDialogProps) {
  return (
    <Dialog {...props}>
      {dialogTrigger && (
        <DialogTrigger className={cn('block size-max', triggerClassName)}>
          {dialogTrigger}
        </DialogTrigger>
      )}
      <DialogPortal container={portalContainer}>
        <DialogOverlay className={cn(dialogVariants({ variant: 'overlay' }))} />
        <DialogPrimitiveContent
          className={cn(dialogVariants({ variant: 'content' }), contentClassName)}
          // 🥷🏼 prevent click outside to close
          onInteractOutside={clickOutsideToClose ? undefined : (e) => e.preventDefault()}
        >
          <DialogTitle className={cn(dialogVariants({ variant: 'title' }), titleClassName)}>
            <div className="flex w-full items-center justify-between">
              <span className="w-full text-center">{title}</span>
              {withCloseButton && (
                <DialogClose className={cn(dialogVariants({ variant: 'close' }))} onClick={onClose}>
                  <X className="stroke-vive-primary-20 hover:stroke-vive-white size-[28px] transition-all"></X>
                </DialogClose>
              )}
            </div>
            {withSeparator && (
              <Separator className="bg-vive-primary-60 absolute bottom-0 w-[calc(100%-48px)]" />
            )}
          </DialogTitle>
          <div className={cn(dialogVariants({ variant: 'children' }), childrenClassName)}>
            {children}
          </div>
        </DialogPrimitiveContent>
      </DialogPortal>
    </Dialog>
  )
}

export default ViverseDialog
